import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

// Import Guards
import { AuthGuard } from './guards/auth/auth.guard';

// Import View Components
import { AdminComponent } from './modules/admin/admin.component';
import { RemoteAccessComponent } from './modules/authentication/remote-access/remote-access.component';
import { SetClientComponent } from './modules/authentication/set-client/set-client.component';
import { SignInComponent } from './modules/authentication/sign-in/sign-in.component';
import { ResetPasswordComponent } from './modules/authentication/reset-password/reset-password.component';
import { SetPasswordComponent } from './modules/authentication/set-password/set-password.component';
import { DashboardComponent } from './modules/dashboard/dashboard.component';
import { ReportComponent } from './modules/report/report.component';
import { ReportDataComponent } from './modules/report/report-data/report-data.component';
import { VirtualTerminalComponent } from './modules/virtual-terminal/virtual-terminal.component';
import { PriceCheckComponent } from './modules/price-check/price-check.component';
import { DetailAccountComponent } from './modules/report/detail-account/detail-account.component';
import { DetailBatchComponent } from './modules/report/detail-batch/detail-batch.component';
import { DetailBillingAccountComponent } from './modules/report/detail-billing-account/detail-billing-account.component';
import { DetailCustomerComponent } from './modules/report/detail-customer/detail-customer.component';
import { DetailDiscountComponent } from './modules/report/detail-discount/detail-discount.component';
import { DetailDynamicComponent } from './modules/report/detail-dynamic/detail-dynamic.component';
import { DetailInventoryComponent } from './modules/report/detail-inventory/detail-inventory.component';
import { DetailInventoryBasicComponent } from './modules/report/detail-inventory-basic/detail-inventory-basic.component';
import { DetailLocationComponent } from './modules/report/detail-location/detail-location.component';
import { DetailVendorComponent } from './modules/report/detail-vendor/detail-vendor.component';
import { DetailPurchaseComponent } from './modules/report/detail-purchase/detail-purchase.component';
import { DetailReportComponent } from './modules/report/detail-report/detail-report.component';
import { DetailTransactionComponent } from './modules/report/detail-transaction/detail-transaction.component';
import { DetailUserComponent } from './modules/report/detail-user/detail-user.component';
import { DetailUserAuthorizationComponent } from './modules/report/detail-user-authorization/detail-user-authorization.component';
import { DetailUserGroupComponent } from './modules/report/detail-user-group/detail-user-group.component';
import { DetailTaxComponent } from './modules/report/detail-tax/detail-tax.component';
import { DetailPermissionComponent } from './modules/report/detail-permission/detail-permission.component';
import { DetailGroupComponent } from './modules/report/detail-group/detail-group.component';
import { DetailEmployeeComponent } from './modules/report/detail-employee/detail-employee.component';
import { DetailTerminalComponent } from './modules/report/detail-terminal/detail-terminal.component';
import { DetailPermissionGroupComponent } from './modules/report/detail-permission-group/detail-permission-group.component';
import { DetailReturnTypeComponent } from './modules/report/detail-return-type/detail-return-type.component';
import { DetailVendorInventoryComponent } from './modules/report/detail-vendor-inventory/detail-vendor-inventory.component';
import { TemplateComponent } from './modules/template/template.component';
import { TransferComponent } from './modules/transfer/transfer.component';
import { DetailMenuActionComponent } from './modules/report/detail-menu-action/detail-menu-action.component';
import { DetailDiscountGroupComponent } from './modules/report/detail-discount-group/detail-discount-group.component';
import { DiscountWizardComponent } from './modules/report/discount-wizard/discount-wizard.component';
import { DetailInvoiceComponent } from './modules/report/detail-invoice/detail-invoice.component';
import { DetailViewComponent } from './modules/report/detail-view/detail-view.component';
import { DetailViewListComponent } from './modules/report/detail-view-list/detail-view-list.component';
import { DistributorManagementComponent } from './modules/report/distributor-management/distributor-management.component';
import { ProductOverviewComponent } from './modules/report/product-overview/product-overview.component';
import { EditorListComponent } from './modules/editor/editor-list/editor-list.component';
import { ReportEditorComponent } from './modules/editor/report-editor/report-editor.component';
import { ViewEditorComponent } from './modules/editor/view-editor/view-editor.component';


const routes: Routes = [
  {
    path: 'set-client',
    component: SetClientComponent
  },
  {
    path: 'remote-access',
    component: RemoteAccessComponent
  },
  {
    path: 'sign-in',
    component: SignInComponent
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent
  },
  {
    path: 'set-password',
    component: SetPasswordComponent
  },
  {
    path: 'dashboard',
    redirectTo: 'reports',
    pathMatch: 'full'
  },
  {
    path: '',
    component: DashboardComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'reports',
    component: ReportComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: ':key',
        component: ReportDataComponent,
        children: [
          {
            path: 'account/:documentId',
            component: DetailAccountComponent
          },
          {
            path: 'batch/:documentId',
            component: DetailBatchComponent
          },
          {
            path: 'billing-account/:documentId',
            component: DetailBillingAccountComponent
          },
          {
            path: 'customer/:documentId',
            component: DetailCustomerComponent
          },
          {
            path: 'discount/:documentId',
            component: DetailDiscountComponent
          },
          {
            path: 'dynamic/:documentId',
            component: DetailDynamicComponent
          },
          {
            path: 'inventory/:documentId',
            component: DetailInventoryComponent
          },
          {
            path: 'inventory-basic/:documentId',
            component: DetailInventoryBasicComponent
          },
          {
            path: 'location/:documentId',
            component: DetailLocationComponent
          },
          {
            path: 'vendor/:documentId',
            component: DetailVendorComponent
          },
          {
            path: 'purchase/:documentId',
            component: DetailPurchaseComponent
          },
          {
            path: 'report/:documentId',
            component: DetailReportComponent
          },
          {
            path: 'transaction/:documentId',
            component: DetailTransactionComponent
          },
          {
            path: 'user/:documentId',
            component: DetailUserComponent
          },
          {
            path: 'user-group/:documentId',
            component: DetailUserGroupComponent
          },
          {
            path: 'user-authorization/:documentId',
            component: DetailUserAuthorizationComponent
          },
          {
            path: 'employee/:documentId',
            component: DetailEmployeeComponent
          },
          {
            path: 'tax/:documentId',
            component: DetailTaxComponent
          },
          {
            path: 'permission/:documentId',
            component: DetailPermissionComponent
          },
          {
            path: 'group/:documentId',
            component: DetailGroupComponent
          },
          {
            path: 'permissionGroup/:documentId',
            component: DetailPermissionGroupComponent
          },
          {
            path: 'return-type/:documentId',
            component: DetailReturnTypeComponent
          },
          {
            path: 'credit-item/:documentId',
            component: DetailReturnTypeComponent
          },
          {
            path: 'terminal/:documentId',
            component: DetailTerminalComponent
          },
          {
            path: 'vendor-product/:documentId',
            component: DetailVendorInventoryComponent
          },
          {
            path: 'menu-actions/:documentId',
            component: DetailMenuActionComponent
          },
          {
            path: 'discountGroup/:documentId',
            component: DetailDiscountGroupComponent
          },
          {
            path: 'discount-wizard/:documentId',
            component: DiscountWizardComponent
          },
          {
            path: 'invoice/:documentId',
            component: DetailInvoiceComponent
          },
          {
            path: 'details/:viewKey/:documentId',
            component: DetailViewComponent
          },
          {
            path: 'details/:viewKey/:documentId/vendors',
            component: DistributorManagementComponent
          },
          {
            path: 'details/:viewKey/:documentId/overview',
            component: ProductOverviewComponent
          },
          {
            path: 'bulk/:viewKey/:listKey',
            component: DetailViewListComponent
          }
        ]
      }
    ]
  },
  {
    path: 'templates/:templateId',
    component: TemplateComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'transfers',
    component: TransferComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'transfers/:purchaseTypeKey',
    component: TransferComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'details/:viewKey/:documentId',
        component: DetailViewComponent
      }
    ]
  },
  {
    path: 'transfers/:purchaseTypeKey/:purchaseId',
    component: TransferComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'details/:viewKey/:documentId',
        component: DetailViewComponent
      }
    ]
  },
  {
    path: 'price-check',
    component: PriceCheckComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'virtual-terminal',
    component: VirtualTerminalComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'virtual-terminal/:ledgerKey',
    component: VirtualTerminalComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'virtual-terminal/:ledgerKey/:action',
    component: VirtualTerminalComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'editor/reports',
    component: EditorListComponent,
    canActivate: [AuthGuard],
    data: { editorType: 'report' }
  },
  {
    path: 'editor/reports/:documentId',
    component: ReportEditorComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'editor/views',
    component: EditorListComponent,
    canActivate: [AuthGuard],
    data: { editorType: 'view' }
  },
  {
    path: 'editor/views/:documentId',
    component: ViewEditorComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'admin',
    component: AdminComponent,
    canActivate: [AuthGuard]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
